import { useState } from "react";

import styles from "./PrPanel.module.css";
import {
  type ApiBuildStatus,
  useTaskBuildStatuses,
} from "../../data/api/buildStatuses";
import { useTaskPullRequest } from "../../data/api/pullRequests";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

// The kind of a status for colouring: success-like → done, failing-like →
// needs-input, otherwise pending. Mirrors the server's classification
// (build-status.md §Aggregate checks summary).
const FAILING = new Set([
  "action_required",
  "cancelled",
  "error",
  "failure",
  "stale",
  "startup_failure",
  "timed_out",
]);
const PASSING = new Set(["neutral", "skipped", "success"]);

const tone = (s: ApiBuildStatus): "done" | "needs-input" | "pending" => {
  const v = (s.conclusion ?? s.state).toLowerCase();
  if (PASSING.has(v)) {
    return "done";
  }
  if (FAILING.has(v)) {
    return "needs-input";
  }
  return "pending";
};

// Human duration between start and completion, e.g. "1m 4s".
const duration = (s: ApiBuildStatus): string | null => {
  if (!s.started_at || !s.completed_at) {
    return null;
  }
  const ms = Date.parse(s.completed_at) - Date.parse(s.started_at);
  if (!Number.isFinite(ms) || ms < 0) {
    return null;
  }
  const secs = Math.round(ms / 1000);
  return secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
};

// Latest status per context (rows arrive newest-first), so retries collapse to
// the current result — the grouping the spec calls for (§Build statuses on a PR).
const latestByContext = (
  statuses: ReadonlyArray<ApiBuildStatus>,
): ReadonlyArray<ApiBuildStatus> => {
  const seen = new Set<string>();
  const out: ApiBuildStatus[] = [];
  for (const s of statuses) {
    if (!seen.has(s.context)) {
      seen.add(s.context);
      out.push(s);
    }
  }
  return out;
};

export type PrPanelProps = {
  readonly taskId: string;
};

// The PR side panel in the diff tab: PR metadata plus the PR head's build
// statuses grouped by context (specifications/diffs-and-pull-requests.md
// §Build statuses on a PR). Renders nothing until a PR exists.
export const PrPanel = ({ taskId }: PrPanelProps) => {
  const { pullRequest: pr } = useTaskPullRequest(taskId);
  const { statuses } = useTaskBuildStatuses(taskId);
  // Closed to start with: the diff is what the tab is for, and the header row
  // already carries the number, state and title, so the body is detail to reach
  // for rather than something worth spending vertical space on by default.
  const [open, setOpen] = useState(false);

  if (!pr) {
    return null;
  }
  const checks = latestByContext(statuses);

  return (
    <div className={styles.panel}>
      <button
        aria-expanded={open}
        className={styles.head}
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <Icon.ChevDown
          className={cx(styles.chev, !open && styles.chevClosed)}
          size={13}
        />
        <Icon.PullRequest size={13} />
        <span className={styles.num}>#{pr.number}</span>
        <span className={cx(styles.state, styles[`state_${pr.state}`])}>
          {pr.state}
        </span>
        <span className={styles.title}>{pr.title}</span>
        <span className={styles.grow} />
        {pr.html_url ? (
          <a
            className={styles.link}
            href={pr.html_url}
            onClick={(e) => e.stopPropagation()}
            rel="noreferrer"
            target="_blank"
            title="Open on GitHub"
          >
            <Icon.External size={12} />
          </a>
        ) : null}
      </button>

      {open ? (
        <div className={styles.body}>
          <div className={styles.branches}>
            <Icon.Branch size={11} />
            <span className={styles.branch}>{pr.head_branch}</span>
            <Icon.ArrowRight size={11} />
            <span className={styles.branch}>{pr.base_branch}</span>
          </div>

          {checks.length > 0 ? (
            <div className={styles.checks}>
              {checks.map((s) => (
                <div className={styles.check} key={s.public_id}>
                  <span className={cx(styles.dot, styles[`dot_${tone(s)}`])} />
                  <span className={styles.context}>{s.context}</span>
                  <span className={styles.checkState}>
                    {s.conclusion ?? s.state}
                  </span>
                  {duration(s) ? (
                    <span className={styles.dur}>{duration(s)}</span>
                  ) : null}
                  {s.details_url ? (
                    <a
                      className={styles.detail}
                      href={s.details_url}
                      rel="noreferrer"
                      target="_blank"
                      title="Details"
                    >
                      <Icon.External size={11} />
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noChecks}>
              No checks have reported for this commit yet.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
